import React, { useState, useRef, useEffect } from 'react';
import { Flex, Button, Box, Text, Image } from 'rebass';
import { BsArrowRightShort, BsArrowLeftShort } from 'react-icons/bs';
import { CgTemplate, CgCrop } from 'react-icons/cg';
import { BiEditAlt, BiText } from 'react-icons/bi';
import SliderPageRenderer from './SliderComponents/SliderPageRenderer';
import { useMobileCheckHook } from 'story-media/hooks';
import EmmaCover from 'stories/assets/EmmaCover.png';
import { getCategory } from '../helper';
import BookEditorTopBar from './BookEditorTopBar';
import SelectedMediaToolbar from './SelectedMediaToolbar';
import useOnClickOutside from 'use-onclickoutside';

// ─── PageFace ─────────────────────────────────────────────────────────────────
// Renders one page inside the flipbook.
// Shows a lined blank placeholder when `page` is undefined (e.g. odd-count spread).
const PageFace = React.memo(function PageFace({
  page,
  index,
  zoomValue,
  sliderHeight,
  selectedPageIndex,
  setCurrentPage,
  onMediaClick,
  selectedLayer,
  setSelectedLayer,
  heightRef,
  widthRef,
  pointerEvents = 'auto',
  isReadOnly,
  currentPage,
  ...props
}) {
  if (!page) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          bg: '#f5efe0',
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(0,0,0,0.05) 28px)',
        }}
      />
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', bg: '#fff' }}>
      <Box
        sx={{
          transform: `scale(${zoomValue / 100})`,
          transformOrigin: 'top left',
          width: 'max-content',
          height: sliderHeight + 'px',
          pointerEvents,
        }}
        ref={widthRef}
      >
        <Flex ref={heightRef}>
          <SliderPageRenderer
            isFromSlider
            {...props}
            index={index}
            zoom={100}
            {...page}
            attributes={page.attributes || page}
            isSelected={selectedPageIndex === index}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            onMediaClick={onMediaClick}
            selectedLayer={selectedLayer}
            setSelectedLayer={setSelectedLayer}
            isReadOnly={isReadOnly}
          />
        </Flex>
      </Box>
    </Box>
  );
});

// ─── RenderButton ─────────────────────────────────────────────────────────────
const RenderButton = React.memo(function RenderButton({
  onClick,
  icon,
  label,
  background = 'linear-gradient(0deg, rgba(252, 118, 132, 0.10) 0%, rgba(252, 118, 132, 0.10) 100%), #FFF',
  disabled = false,
}) {
  return (
    <Flex
      onClick={onClick}
      sx={{ gap: '6px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
      alignItems="center"
      justifyContent="center"
    >
      <Flex
        p="8px"
        height="max-content"
        justifyContent="center"
        alignItems="center"
        sx={{ cursor: 'pointer', borderRadius: '10px', background }}
      >
        {icon}
      </Flex>
      <Text
        fontFamily="Poppins"
        fontWeight={500}
        fontSize="14px"
        lineHeight="20px"
        color="#FFFFFF"
        width={(label || '').length > 17 ? "100px" : "fit-content"}
      >
        {(label || '')?.length > 23 ? `${(label).slice(0, 23)}…` : (label)}
      </Text>
    </Flex>
  );
});

// ─── FlipbookPanel ────────────────────────────────────────────────────────────
// The dark right panel containing the story header, flipbook, and toolbar.
// All props are passed in so this component stays stateless and reusable.
//
// Props
// ─────
// Layout
//   divWidth            — CSS width string for the fixed panel (e.g. "calc(100vw - 385px)")
//
// Story header
//   storyDetails        — { title, id, ... }
//   member              — { 'display-name': string }
//
// Navigation
//   handlePrev / handleNext        — click handlers for arrow buttons
//   canFlipBackward / canFlipForward — disable/style the buttons
//   selectedPageIndex              — used for Edit Story URL
//   push                           — router push (useHistory)
//
// Book dimensions
//   bookWidth           — px number (2 × targetPageWidth)
//   pageRenderHeight    — px number (sliderHeight × zoomValue/100)
//
// Drag + animation state
//   isAnimating         — true during the CSS flip transition
//   showFlippingElement — true when the flipping layer should be rendered
//   flipDirection       — 'forward' | 'backward' | null
//   flipRotation        — rotation degrees applied to the flipping element
//   onDragStart(clientX)          — pointer-down on the book
//   onDragMove(clientX, pageWidth)— pointer-move
//   onDragEnd()                   — pointer-up / leave
//   pageWidth           — px reference width for rotation mapping
//   setContainerHeight  — state setter; called via ResizeObserver on book container
//
// Static page slots
//   displayLeftPage / displayRightPage  — page data objects (or undefined)
//   leftIdx / rightIdx                  — page indices within totalPagesData
//
// Flipping element
//   flipFrontPage / flipFrontIdx  — page data + index for the front face
//   flipBackPage  / flipBackIdx   — page data + index for the back face
//
// Shared page-rendering props (forwarded to every PageFace)
//   zoomValue, sliderHeight
//   setCurrentPage, onMediaClick
//   selectedLayer, setSelectedLayer
//   handleSliderHeightRef   — ref callback that measures sliderHeight (applied to left slot)
//   setMeasureRef(i, el)    — stores el in the parent's measureRefs at index i
//
// Toolbar
//   pagePossibilitiesData   — layout options array
//   isValidating            — true while page possibilities are loading
//   setChangeTemplate       — opens the layout modal
//   setSelectedImageLayer   — opens the crop modal
export default function FlipbookPanel({
  // Layout
  divWidth,

  // Story header
  storyDetailsLeft,
  storyDetailsRight,
  member,
  isReadOnly,
  // Navigation
  handlePrev,
  handleNext,
  canFlipBackward,
  canFlipForward,
  selectedPageIndex,
  push,

  // Book dimensions
  bookWidth,
  pageRenderHeight,

  // Animation / drag
  isAnimating,
  showFlippingElement,
  flipDirection,
  flipRotation,
  onDragStart,
  // onDragMove,
  // onDragEnd,
  // pageWidth,
  setContainerHeight,

  // Mobile swipe
  onTouchStart,
  onTouchEnd,

  // Static slots
  displayLeftPage,
  displayRightPage,
  leftIdx,
  rightIdx,

  // Flipping element
  flipFrontPage,
  flipFrontIdx,
  flipBackPage,
  flipBackIdx,

  // Shared page-rendering
  zoomValue,
  sliderHeight,
  setCurrentPage,
  onMediaClick,
  selectedLayer,
  setSelectedLayer,
  handleSliderHeightRef,
  setMeasureRef,

  // Toolbar
  leftPagePossibilities,
  rightPagePossibilities,
  leftPageIsValidating,
  rightPageIsValidating,
  setChangeTemplate,
  setSelectedImageLayer,
  currentPage,
  zIndex,
  isFromTellAStory,
  setShowTextModal,
  HeaderComponent,
  // Per-page action buttons
  selectedLayerPageIndex,
  onEditStory,
  onChangeLayout,
  onCropPhoto,
  loading,

  // Move/resize media editing
  mediaLayerEditor,
  editablePageId,
  getLayerOverride,
  hasSelection = false,
  canCropSelection = false,
  leftPageLock,
  rightPageLock,
  canUndo = false,
  canRedo = false,
  onUndo = () => { },
  onRedo = () => { },
  canSaveChanges = false,
  isSavingChanges = false,
  onSaveChanges = () => { },
  checkMarkSuccess,
  setCheckMarkSuccess,
}) {
  const isFromMobile = useMobileCheckHook();
  // 'closed'  → cover centred, waiting for click
  // 'sliding' → cover translating right to right-page position
  // 'opening' → cover flipping open (rotateY 0 → -180)
  // 'open'    → flipbook fully visible, cover overlay removed
  const [bookOpenPhase, setBookOpenPhase] = useState(isReadOnly && isFromTellAStory ? 'closed' : 'open');
  const [coverX, setCoverX] = useState(0);   // px translation (phase 1)
  const [coverRot, setCoverRot] = useState(0); // degrees (phase 2)
  const openTimerRef = useRef(null);
  const openRafRef = useRef(null);
  useEffect(() => () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (openRafRef.current) cancelAnimationFrame(openRafRef.current);
  }, []);

  // A click anywhere outside this whole panel (the book + its toolbars) also
  // clears the active selection — e.g. clicking the thumbnail sidebar, the
  // step header, or the dark area around the panel. Clicks INSIDE the panel
  // (Undo/Redo, Save changes, the lock toggles, Crop Photo, another media
  // group, blank page area) are handled by their own, more specific logic
  // and are never "outside" this ref, so they're unaffected.
  const panelRef = useRef(null);
  useOnClickOutside(panelRef, () => {
    if (mediaLayerEditor?.selectedLayerIds?.length) {
      mediaLayerEditor.clearSelection();
    }
  });

  // Stable ref callback — useCallback prevents a new ResizeObserver being
  // created on every render (which would accumulate and create an infinite loop).
  const containerHeightRef = React.useCallback((el) => {
    if (!el) return;
    setContainerHeight(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(([entry]) => {
      setContainerHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [setContainerHeight]); // setContainerHeight is a stable useState setter

  const coverSwipeTouchStartX = useRef(null);
  const handleCoverTouchStart = (e) => {
    coverSwipeTouchStartX.current = e.touches[0].clientX;
  };
  const handleCoverTouchEnd = (e) => {
    if (coverSwipeTouchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - coverSwipeTouchStartX.current;
    coverSwipeTouchStartX.current = null;
    if (delta > 50) handleOpenBook();
  };

  const handleOpenBook = () => {
    if (bookOpenPhase !== 'closed') return;

    // Phase 1: slide cover from centre to right-page slot
    setBookOpenPhase('sliding');
    openRafRef.current = requestAnimationFrame(() => {
      openRafRef.current = requestAnimationFrame(() => {
        // Right-page slot starts at bookWidth/2 from the container centre.
        // The cover (width = bookWidth/2) is currently centred, so its left
        // edge sits at -bookWidth/4 relative to centre. To land it at the
        // right-page position we translate by bookWidth/4.
        setCoverX(bookWidth / 4);
        openTimerRef.current = setTimeout(() => {
          // Phase 2: flip the cover open
          setBookOpenPhase('opening');
          openRafRef.current = requestAnimationFrame(() => {
            openRafRef.current = requestAnimationFrame(() => {
              setCoverRot(-180);
              openTimerRef.current = setTimeout(() => {
                setBookOpenPhase('open');
              }, 900);
            });
          });
        }, 450);
      });
    });
  };

  return (<>
    <Box sx={{
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 40,
    }}>
      {HeaderComponent}
    </Box>
    <Flex
      ref={panelRef}
      bg="#354A5F"
      width={divWidth}
      justifyContent={isReadOnly ? "center" : "center"}
      sx={{
        position: 'fixed',
        right: 0,
        top: isReadOnly ? [0, 0, 0, 60] : 0,
        transition: 'width 0.3s ease',
        height: '100vh',
        justifyContent: 'flex-start',
        alignItems: 'center',
        zIndex,
      }}
      flexDirection="column"
    >

      {/* ── Lock / Undo / Redo / Save ────────────────────────────────────── */}
      {!isReadOnly && leftPageLock && rightPageLock && (
        <BookEditorTopBar
          leftLock={leftPageLock}
          rightLock={rightPageLock}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={onUndo}
          onRedo={onRedo}
          canSave={canSaveChanges}
          isSaving={isSavingChanges}
          onSaveChanges={onSaveChanges}
          width={bookWidth}
          checkMarkSuccess={checkMarkSuccess}
          setCheckMarkSuccess={setCheckMarkSuccess}
        />
      )}

      {/* ── Prev button ──────────────────────────────────────────────────── */}
      {canFlipBackward && bookOpenPhase === 'open' && <Button
        onClick={handlePrev}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          background: '#fff',
          borderRadius: '5px',
          cursor: canFlipBackward ? 'pointer' : 'not-allowed',
          opacity: canFlipBackward ? 1 : 0.4,
          boxShadow: '0 0 5px 0 rgba(155, 89, 182, 0.15) inset',
          position: 'absolute',
          top: isReadOnly ? ['20%', '50%'] : '50%',
          transform: 'translateY(-50%)',
          left: 10,
          zIndex: 30,
        }}
        p="4px !important"
        height="unset !important"
      >
        <BsArrowLeftShort size={isFromMobile ? 30 : 24} color="#9B59B6" />
      </Button>}

      {/* ── Next button ──────────────────────────────────────────────────── */}
      {(canFlipForward && (bookOpenPhase === 'open' || bookOpenPhase === 'closed')) && <Button
        onClick={bookOpenPhase === 'closed' ? handleOpenBook : handleNext}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          background: '#fff',
          borderRadius: '5px',
          cursor: canFlipForward ? 'pointer' : 'not-allowed',
          opacity: canFlipForward ? 1 : 0.4,
          boxShadow: '0 0 5px 0 rgba(155, 89, 182, 0.15) inset',
          position: 'absolute',
          top: isReadOnly ? ['20%', '50%'] : '50%',
          transform: 'translateY(-50%)',
          right: 10,
          zIndex: 51,
        }}
        p="4px !important"
        height="unset !important"
        color={"#9B59B6"}
      >
        <BsArrowRightShort size={isFromMobile ? 30 : 24} color="#9B59B6" />
      </Button>}
      {/* ── Flipbook ─────────────────────────────────────────────────────── */}
      {/* Relative wrapper anchors the cover overlay over the flipbook area.
          Only a small breathing gap below BookEditorTopBar/the nav buttons —
          the book's own size (via zoomValue + the maxHeight below) already
          accounts for that chrome's height, so this must stay small or the
          book gets pushed past the bottom of the viewport and clips. */}
      <Box sx={{
        m: 'auto', mt: "200px",
        mb: "20px", position: 'relative', width: '100%', display: 'flex', justifyContent: 'center'
      }}>

        {/* ── Book cover (shown until opened) ─────────────────────────────── */}
        {isReadOnly && bookOpenPhase !== 'open' && (
          <Box
            onTouchStart={bookOpenPhase === 'closed' ? handleCoverTouchStart : undefined}
            onTouchEnd={bookOpenPhase === 'closed' ? handleCoverTouchEnd : undefined}
            sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              zIndex: 50,
              perspective: '1200px',
              perspectiveOrigin: 'center top',
              pointerEvents: bookOpenPhase === 'opening' || bookOpenPhase === 'sliding' ? 'none' : 'auto',
            }}
          >
            <Box
              onClick={handleOpenBook}
              sx={{
                position: 'relative',
                width: bookWidth > 0 ? `${bookWidth / 2}px` : '220px',
                height: pageRenderHeight > 0 ? `${pageRenderHeight}px` : '380px',
                transformOrigin: 'left center',
                transformStyle: 'preserve-3d',
                transform: `translateX(${coverX}px) rotateY(${coverRot}deg)`,
                transition: bookOpenPhase === 'sliding'
                  ? 'transform 0.4s ease'
                  : bookOpenPhase === 'opening'
                    ? 'transform 0.8s cubic-bezier(0.645, 0.045, 0.355, 1.000)'
                    : 'none',
                cursor: bookOpenPhase === 'closed' ? 'pointer' : 'default',
                boxShadow: '0 24px 24px rgba(0,0,0,0.6), 4px 0 12px rgba(0,0,0,0.3)',
              }}
            >
              {/* Front face: the cover image */}
              <Box sx={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', overflow: 'hidden' }}>
                <Image
                  src={EmmaCover}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {/* Left-edge spine shadow */}
                <Box sx={{
                  position: 'absolute', top: 0, bottom: 0, left: 0, width: '30px',
                  background: 'linear-gradient(to right, rgba(0,0,0,0.35), transparent)',
                  pointerEvents: 'none',
                }} />
              </Box>
              {/* Back face: inside of cover revealed mid-flip */}
              <Box sx={{
                position: 'absolute', inset: 0,
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                bg: '#fff',
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(0,0,0,0.05) 28px)',
                boxShadow: '-6px 0 20px rgba(0,0,0,0.12) inset',
              }} />
            </Box>
          </Box>
        )}

        {/* Perspective wrapper — required for CSS 3D to render correctly */}
        <Box
          sx={{
            perspective: '2000px',
            perspectiveOrigin: 'center center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            // Keep flipbook hidden (layout preserved) until the cover finishes sliding
            visibility: isReadOnly && (bookOpenPhase === 'closed' || bookOpenPhase === 'sliding') ? 'hidden' : 'visible',
          }}
        >
          {/* Book container */}
          <Box
            className="slider-container"
            sx={{
              position: 'relative',
              width: bookWidth > 0 ? bookWidth + 'px' : '100vw',
              height: pageRenderHeight > 0 ? pageRenderHeight + 'px' : 'auto',
              // Must stay consistent with the `reservedVertical` values in
              // PageSlider's zoomValue memo (readonly: 80px; editing: 180px —
              // BookEditorTopBar + the small gap above the book, plus the
              // per-page button row/margins below it) so the container never
              // clips a page that the zoom calc already sized to fit.
              maxHeight: isReadOnly ? 'calc(100vh - 80px)' : 'calc(100vh - 180px)',
              overflow: 'hidden',
              willChange: 'transform',
              boxShadow: (bookOpenPhase !== 'sliding' && bookOpenPhase !== 'opening') && '0 24px 24px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.18)',
              transformStyle: 'preserve-3d',
              cursor: isAnimating ? 'not-allowed' : 'default',
              borderRadius: '10px',
            }}
            ref={containerHeightRef}
            // onMouseMove={onDragMove ? (e) => onDragMove(e.clientX, pageWidth) : undefined}
            // onMouseUp={onDragEnd}
            // onMouseLeave={onDragEnd}
            // onTouchMove={onDragMove ? (e) => { e.preventDefault(); onDragMove(e.touches[0].clientX, pageWidth); } : undefined}
            // onTouchEnd={onDragEnd}
            // onMouseDown={onDragStart ? (e) => { e.preventDefault(); onDragStart(e.clientX); } : undefined}
            // onTouchStart={onDragStart ? (e) => onDragStart(e.touches[0].clientX) : undefined}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {/* ── Left page slot ───────────────────────────────────────────── */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                display: (bookOpenPhase === 'sliding' || bookOpenPhase === 'opening') && 'none !important',
                width: '50%',
                height: '100%',
                overflow: 'hidden',
                bg: '#fff',
                borderRight: '1px solid rgba(0,0,0,0.07)',
                // Bottom-left corner shadow — page-curl hint for backward navigation
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '70px',
                  height: '70px',
                  background: 'radial-gradient(circle at bottom left, rgba(0,0,0,0.18) 0%, transparent 70%)',
                  pointerEvents: 'none',
                  zIndex: 5,
                },
              }}
            >
              <PageFace
                storyDetails={storyDetailsLeft?.title}
                page={displayLeftPage}
                index={leftIdx}
                zoomValue={zoomValue}
                sliderHeight={sliderHeight}
                selectedPageIndex={-1}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                onMediaClick={(layer) => onMediaClick(layer, leftIdx)}
                selectedLayer={selectedLayer}
                setSelectedLayer={setSelectedLayer}
                heightRef={handleSliderHeightRef}
                widthRef={(el) => setMeasureRef(0, el)}
                isReadOnly={isReadOnly}
                layerEditor={
                  editablePageId && (displayLeftPage?.id || displayLeftPage?.attributes?.id) === editablePageId
                    ? mediaLayerEditor
                    : undefined
                }
                getPositionOverride={
                  getLayerOverride
                    ? id => getLayerOverride(displayLeftPage?.id || displayLeftPage?.attributes?.id, id)
                    : undefined
                }
              />
            </Box>

            {/* ── Right page slot ──────────────────────────────────────────── */}
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                width: '50%',
                height: '100%',
                overflow: 'hidden',
                bg: '#fff',
                boxShadow: '0 88.126px 88.126px 0 rgba(0,0,0,0.30), 88.126px 0 88.126px 0 rgba(0,0,0,0.30) inset',
                // Bottom-right corner shadow — page-curl hint for forward navigation
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '70px',
                  height: '70px',
                  background: 'radial-gradient(circle at bottom right, rgba(0,0,0,0.18) 0%, transparent 70%)',
                  pointerEvents: 'none',
                  zIndex: 5,
                },
              }}
            >
              <PageFace
                storyDetails={storyDetailsRight?.title}
                page={displayRightPage}
                index={rightIdx}
                zoomValue={zoomValue}
                sliderHeight={sliderHeight}
                selectedPageIndex={-1}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                onMediaClick={(layer) => onMediaClick(layer, rightIdx)}
                selectedLayer={selectedLayer}
                setSelectedLayer={setSelectedLayer}
                widthRef={(el) => setMeasureRef(1, el)}
                isReadOnly={isReadOnly}
                layerEditor={
                  editablePageId && (displayRightPage?.id || displayRightPage?.attributes?.id) === editablePageId
                    ? mediaLayerEditor
                    : undefined
                }
                getPositionOverride={
                  getLayerOverride
                    ? id => getLayerOverride(displayRightPage?.id || displayRightPage?.attributes?.id, id)
                    : undefined
                }
              />
            </Box>

            {/* ── Spine ────────────────────────────────────────────────────── */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: '6px',
                transform: 'translateX(-50%)',
                zIndex: 10,
                pointerEvents: 'none',
                background: 'linear-gradient(to right, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.05) 60%, rgba(0,0,0,0.18) 100%)',
              }}
            />

            {/* ── Flipping element ─────────────────────────────────────────── */}
            {showFlippingElement && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: flipDirection === 'forward' ? '50%' : 0,
                  width: '50%',
                  height: '100%',
                  transformOrigin: flipDirection === 'forward' ? 'left center' : 'right center',
                  transform: `rotateY(${flipRotation}deg)`,
                  transition: isAnimating
                    ? 'transform 0.6s cubic-bezier(0.645, 0.045, 0.355, 1.000)'
                    : 'none',
                  transformStyle: 'preserve-3d',
                  zIndex: 20,
                  willChange: 'transform',
                }}
              >
                {/* Front face — page that is leaving */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backfaceVisibility: 'hidden',
                    overflow: 'hidden',
                    bg: '#fff',
                  }}
                >
                  <PageFace
                    storyDetails={storyDetailsLeft?.title}
                    page={flipFrontPage}
                    index={flipFrontIdx}
                    zoomValue={zoomValue}
                    sliderHeight={sliderHeight}
                    selectedPageIndex={selectedPageIndex}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    onMediaClick={onMediaClick}
                    selectedLayer={selectedLayer}
                    setSelectedLayer={setSelectedLayer}
                    pointerEvents="none"
                    isReadOnly={isReadOnly}
                  />
                  {/* Page-curl shadow on the lifting edge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      [flipDirection === 'forward' ? 'left' : 'right']: 0,
                      width: '50px',
                      background:
                        flipDirection === 'forward'
                          ? 'linear-gradient(to right, rgba(0,0,0,0.20), transparent)'
                          : 'linear-gradient(to left, rgba(0,0,0,0.20), transparent)',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>

                {/* Back face — page that is arriving */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    overflow: 'hidden',
                    bg: '#fff',
                  }}
                >
                  <PageFace
                    storyDetails={storyDetailsRight?.title}
                    page={flipBackPage}
                    index={flipBackIdx}
                    zoomValue={zoomValue}
                    sliderHeight={sliderHeight}
                    selectedPageIndex={selectedPageIndex}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    onMediaClick={onMediaClick}
                    selectedLayer={selectedLayer}
                    setSelectedLayer={setSelectedLayer}
                    pointerEvents="none"
                    isReadOnly={isReadOnly}
                  />
                  {/* Shadow on the back face's exposed edge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      [flipDirection === 'forward' ? 'right' : 'left']: 0,
                      width: '30px',
                      background:
                        flipDirection === 'forward'
                          ? 'linear-gradient(to left, rgba(0,0,0,0.10), transparent)'
                          : 'linear-gradient(to right, rgba(0,0,0,0.10), transparent)',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>{/* end relative wrapper */}

      {/* ── Per-page action buttons ──────────────────────────────────────── */}
      {!isReadOnly && hasSelection && (
        <Flex
          mt="20px"
          mb="30px"
          sx={{
            width: bookWidth > 0 ? bookWidth + 'px' : '100%',
            maxWidth: '100%',
            flexShrink: 0,
          }}
        >
          <SelectedMediaToolbar canCrop={canCropSelection} onCropPhoto={onCropPhoto} width={bookWidth} />
        </Flex>
      )}
      {!isReadOnly && !hasSelection && (
        <Flex
          mt="20px"
          mb="30px"
          // mb="5%"

          sx={{
            width: bookWidth > 0 ? bookWidth + 'px' : '100%',
            maxWidth: '100%',
            flexShrink: 0,
          }}
        >
          {[
            { page: displayLeftPage, idx: leftIdx, possibilities: leftPagePossibilities, validating: leftPageIsValidating, isFromLeft: true },
            { page: displayRightPage, idx: rightIdx, possibilities: rightPagePossibilities, validating: rightPageIsValidating },
          ].map(({ page, idx, possibilities, validating, isFromLeft }, index) => (
            <Flex
              key={idx}
              sx={{ width: '50%', gap: '12px', flexWrap: 'wrap', justifyContent: index === 0 ? "flex-start" : "flex-end" }}
              alignItems="center"
            >
              {(getCategory(page?.kind) === null && isFromLeft && !validating) && (
                <Flex
                  onClick={(validating || possibilities.length < 2) ? undefined : () => onChangeLayout(idx, page)}
                  sx={{
                    gap: '6px',
                    cursor: (validating || possibilities.length < 2) ? 'not-allowed' : 'pointer',
                    opacity: (validating || possibilities.length < 2) ? 0.5 : 1
                  }}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Flex
                    p="8px"
                    height="max-content"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ borderRadius: '10px', background: 'linear-gradient(0deg, rgba(227, 106, 186, 0.10) 0%, rgba(227, 106, 186, 0.10) 100%), #FFF;' }}
                  >
                    <CgTemplate size={20} color="#E36ABA" />
                  </Flex>
                  <Text fontFamily="Poppins" fontWeight={500} fontSize="14px" lineHeight="20px" color="#FFFFFF">
                    Change page layout
                    {/* {!validating && possibilities?.length === 0 ? <> Unavailable</> : ''} */}
                  </Text>
                </Flex>
              )}
              {/* Edit Story */}
              <RenderButton
                icon={<BiEditAlt size={20} color="#4AA9FF" />}
                label={'Edit story'}
                disabled={getCategory(page?.kind) === null ? !(isFromLeft ? storyDetailsLeft : storyDetailsRight)?.id : false}
                background="linear-gradient(0deg, rgba(28, 83, 135, 0.1) 0%, rgba(74, 169, 255, 0.10) 100%), #FFF"
                onClick={(isFromLeft ? storyDetailsLeft : storyDetailsRight)?.id ? (e) => onEditStory(e, idx, isFromLeft) : (e) => {
                  if (getCategory(page?.kind)) {
                    onEditStory(e, idx, isFromLeft, getCategory(page?.kind))
                  }
                }}
              />


              {/* Change Layout — shown only when this page has layout options */}
              {(getCategory(page?.kind) === null && !isFromLeft && !validating) && (
                <Flex
                  onClick={(validating || possibilities.length < 2) ? undefined : () => onChangeLayout(idx, page)}
                  sx={{
                    gap: '6px',
                    cursor: (validating || possibilities.length < 2) ? 'not-allowed' : 'pointer',
                    opacity: (validating || possibilities.length < 2) ? 0.5 : 1
                  }}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Flex
                    p="8px"
                    height="max-content"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ borderRadius: '10px', background: 'linear-gradient(0deg, rgba(227, 106, 186, 0.10) 0%, rgba(227, 106, 186, 0.10) 100%), #FFF;' }}
                  >
                    <CgTemplate size={20} color="#E36ABA" />
                  </Flex>
                  <Text fontFamily="Poppins" fontWeight={500} fontSize="14px" lineHeight="20px" color="#FFFFFF">
                    Change page layout
                    {/* {!validating && possibilities?.length === 0 ? <> Unavailable</> : ''} */}
                  </Text>
                </Flex>
              )}

              {/* Crop Photo — only when an image layer on this specific page is selected */}
              {getCategory(page?.kind) === null && selectedLayerPageIndex === idx && selectedLayer?.['source-media']?.type === 'images' && (
                <RenderButton
                  icon={<CgCrop size={20} color="#F97316" />}
                  label="Crop Photo"
                  onClick={onCropPhoto}
                />
              )}
            </Flex>
          ))}
        </Flex>
      )}
      {/* {(!loading) &&  */}
      <Text fontFamily="Poppins" fontWeight={400} fontSize="16px" lineHeight="24px" color={isReadOnly ? "#FFFFFF" : "#D9D9D9"} m="40px" mb="100px" mt="20px" textAlign="center" sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }} >
        {isReadOnly ? 'Preview only — you’ll be able to crop photos, select layouts and finalize your pages in your Book Module.' : <>
          Tip: Click on a photo to crop. Videos can't be cropped.
          {/* Close <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="#D9D9D9" />
            <path d="M4 4L12 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="#D9D9D9" />
          </svg> */}
        </>}
      </Text>
      {/* } */}
    </Flex>
  </>
  );
}
